import { Components } from '.generated/templates/Feature.Components.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import Button from 'components/helpers/Button';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import HeadingTag from 'components/helpers/HeadingTag';
import ImageWrapper from 'components/helpers/ImageWrapper/ImageWrapper';
import RichTextA11yWrapper from 'components/helpers/RichTextA11yWrapper';
import useExperienceEditor from 'lib/sitecore/use-experience-editor';
import { classnames } from 'tailwindcss-classnames';
import { getEnumValue } from 'lib/util/enum';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import VideoWrapper from 'components/helpers/VideoWrapper/VideoWrapper';

export type NewHomepageHeroProps = Sitecore.Override.ComponentBase &
  Components.Heroes.NewHomepageHero.Fields.NewHomepageHero;

const NewHomepageHero = ({ fields }: NewHomepageHeroProps): JSX.Element => {
  const textColor = `text-white`;
  const textCenter = `text-center`;
  const videoSrc = '/static/video/Homepage_Sizzle_Final_WEBM.webm';
  const isTopContent = getEnumValue(fields?.contentLocation) === 'top' ? true : false;
  const isImageContent = getEnumValue(fields?.contentType) === 'image' ? true : false;
  const gradientDirection = isTopContent ? `hero-gradient-bottom-top` : `hero-gradient-top-bottom`;
  const isExperienceEditor = useExperienceEditor();
  const locationClasses = isTopContent
    ? classnames('justify-start', 'pt-6', 'md:pt-12')
    : classnames('justify-end', 'pb-6', 'md:pb-12');
  // Fail out if we don't have any fields
  if (!fields) {
    return <MissingDataSource />;
  }

  const cta1Disabled = !!fields.cta1Disable?.value;
  const cta2Disabled = !!fields.cta2Disable?.value;

  const isVideoControlDisabled = !!fields.isVideoControlDisabled?.value;

  // HACK: override image width to 1080px so it responds correctly
  const nextImageField = {
    ...fields.image,
    value: {
      ...fields.image.value,
      width: `1920`,
      height: `1080`,
    },
  };

  return (
    <>
      <div
        className={classnames('overflow-hidden')}
        data-component="authorable/Heroes/newhomepagehero"
      >
        <div
          className={classnames(
            'max-w-screen-2xl',
            'flex',
            'flex-col',
            'md:px-20',
            'px-4',
            'pt-6',
            'items-start',
            'gap-2.5',
            'mx-auto'
          )}
        >
          <div
            className={classnames(
              'flex',
              'relative',
              'overflow-hidden',
              'h-[500px]',
              'md:h-[540px]',
              'rounded-[30px]',
              'w-full',
              'bg-blue-navy'
            )}
          >
            {isImageContent ? (
              <ImageWrapper className={classnames('object-cover')} field={nextImageField} />
            ) : (
              <VideoWrapper
                source={videoSrc}
                isTopControl={!isTopContent}
                isDisabled={isVideoControlDisabled}
              />
            )}
            <div className={classnames('hero-gradient', gradientDirection)} />
            <div
              className={classnames(
                'absolute',
                'flex',
                'h-full',
                'md:mx-12',
                'mx-6',
                'flex-col',
                locationClasses,
                'lg:w-[680px]',
                'max-w-[680px]'
              )}
            >
              <div>
                <HeadingTag
                  headingLevel="h1"
                  className={classnames(
                    textColor,
                    'mb-4',
                    'line-clamp-5',
                    'lg:line-clamp-4',
                    'text-2xl',
                    'font-semibold',
                    'md:text-5xl',
                    'leading-8.1',
                    'md:leading-11.9'
                  )}
                >
                  <Text tag="" field={fields.heroHeadline} />
                </HeadingTag>
                <RichTextA11yWrapper
                  className={classnames(
                    textColor,
                    'mb-4',
                    `line-clamp-6`,
                    `lg:line-clamp-4`,
                    'text-base',
                    'leading-4'
                  )}
                  field={fields.description}
                />
              </div>
              {isExperienceEditor || fields.cta1.value.href || fields.cta2.value.href ? (
                <div
                  className={classnames(
                    'flex',
                    'flex-wrap',
                    'lg:flex-nowrap',
                    'lg:flex-row',
                    'w-full'
                  )}
                >
                  <>
                    <Button
                      linkField={fields.cta1}
                      className={classnames(
                        textCenter,
                        'hover:bg-white',
                        'hover:text-blue-navy',
                        'focus:bg-white',
                        'focus:text-blue-navy',
                        'text-white',
                        'mr-6'
                      )}
                      variant="heroPrimary"
                      disabled={cta1Disabled}
                    />
                    <Button
                      linkField={fields.cta2}
                      className={classnames(
                        textCenter,
                        'text-white',
                        'my-0.5',
                        'hover:text-blue-morning-tint',
                        'focus-visible:outline-white'
                      )}
                      variant="heroSecondary"
                      disabled={cta2Disabled}
                    />
                  </>
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewHomepageHero;
